import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const SLOT_LABEL = {
  leaderboard:        "Leaderboard · 728 × 90",
  sidebar_tall:       "Half-Page · 300 × 600",
  rectangle:          "Rectangle · 300 × 250",
  sidebar_sticky:     "Sidebar Sticky · 300 × 250",
  sponsored_category: "Sponsored Category",
};

const STATUS_COLOR = {
  draft: "#6B7C82",
  pending_review: "#F59E0B",
  active: "#0AADA0",
  expired: "#6B7C82",
  rejected: "#E63946",
  deactivated: "#6B7C82",
};

export const SelfServeAdsTab = () => {
  const [enabled, setEnabled] = useState(false);
  const [pending, setPending] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [tNew, setTNew] = useState({ business_name: "", island: "", quote: "" });
  const [rejectingId, setRejectingId] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const loadAll = async () => {
    try {
      const [s, orders, price, test, adv, rev] = await Promise.all([
        api.get("/admin/settings/self-serve-ads"),
        api.get("/admin/self-serve/orders"),
        api.get("/admin/self-serve/pricing"),
        api.get("/admin/self-serve/testimonials"),
        api.get("/admin/self-serve/advertisers"),
        api.get("/admin/self-serve/revenue"),
      ]);
      setEnabled(!!s.data.enabled);
      setAllOrders(orders.data || []);
      setPending((orders.data || []).filter((o) => o.status === "pending_review"));
      setPricing(price.data || []);
      setTestimonials(test.data || []);
      setAdvertisers(adv.data || []);
      setRevenue(rev.data);
    } catch (e) {
      toast.error("Failed to load self-serve data");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const toggle = async (v) => {
    try {
      await api.put("/admin/settings/self-serve-ads", { enabled: v });
      setEnabled(v);
      toast.success(v ? "Self-Serve Advertiser Portal ON" : "Self-Serve Advertiser Portal OFF");
    } catch (e) { toast.error("Toggle failed"); }
  };

  const approve = async (id) => {
    await api.post(`/admin/self-serve/orders/${id}/approve`);
    toast.success("Approved");
    loadAll();
  };
  const rejectOpen = (id) => { setRejectingId(id); setRejectReason(""); };
  const rejectSubmit = async () => {
    await api.post(`/admin/self-serve/orders/${rejectingId}/reject`, { reason: rejectReason });
    toast.success("Rejected");
    setRejectingId("");
    loadAll();
  };
  const deactivate = async (id) => {
    await api.post(`/admin/self-serve/orders/${id}/deactivate`);
    loadAll();
  };
  const del = async (id) => {
    if (!confirm("Delete this order?")) return;
    await api.delete(`/admin/self-serve/orders/${id}`);
    loadAll();
  };

  const savePrice = async (row) => {
    await api.put("/admin/self-serve/pricing", row);
    toast.success("Price saved");
    loadAll();
  };
  const addTest = async () => {
    if (!tNew.business_name || !tNew.quote) return;
    await api.post("/admin/self-serve/testimonials", { ...tNew, is_active: true });
    setTNew({ business_name: "", island: "", quote: "" });
    loadAll();
  };
  const delTest = async (id) => {
    await api.delete(`/admin/self-serve/testimonials/${id}`);
    loadAll();
  };

  return (
    <div className="space-y-6" data-testid="self-serve-admin">
      {/* Master toggle */}
      <div className="card-soft p-5" data-testid="ss-master-toggle-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-medium text-[#1A1A1A]">Self-Serve Advertiser Portal</h3>
            <p className="text-[12px] text-[#6B7280] mt-0.5">
              When OFF, /advertise and /advertiser/* return 404. Manually-managed ad slots remain unaffected.
            </p>
          </div>
          <label className="flex items-center gap-2 text-[13px]">
            <span className={enabled ? "text-[#0AADA0]" : "text-[#6B7280]"}>{enabled ? "ON" : "OFF"}</span>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => toggle(e.target.checked)}
              data-testid="ss-master-toggle"
            />
          </label>
        </div>
      </div>

      {/* Revenue */}
      {revenue && (
        <div className="card-soft p-5" data-testid="ss-revenue">
          <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">Self-Serve Revenue</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
            <Stat label="This Week"  v={`$${revenue.week} USD`} />
            <Stat label="This Month" v={`$${revenue.month} USD`} />
            <Stat label="All Time"   v={`$${revenue.all_time} USD`} />
            <Stat label="Avg Order"  v={`$${revenue.avg_order_value.toFixed(0)} USD`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <BarBlock title="Revenue by Slot"   data={revenue.by_slot} />
            <BarBlock title="Revenue by Island" data={revenue.by_island} />
          </div>
        </div>
      )}

      {/* Pending review */}
      <div className="card-soft p-5" data-testid="ss-pending">
        <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">
          Pending Review ({pending.length})
        </h3>
        {pending.length === 0 ? (
          <div className="text-[12px] text-[#6B7280]">Nothing awaiting review.</div>
        ) : pending.map((o) => (
          <div key={o.order_id} className="border border-[#EEEEEE] rounded-lg p-3 mb-2" data-testid={`pending-${o.order_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[13px]">
                <b>{o.business_name}</b> · {SLOT_LABEL[o.slot_type]} · {(o.island_targeting || []).join(", ")} ·
                {" "}{o.duration_weeks}w · ${o.price_usd} USD
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => approve(o.order_id)}
                  className="text-[12px] bg-[#0AADA0] text-white px-3 py-1 rounded-full"
                  data-testid={`approve-${o.order_id}`}
                >Approve →</button>
                <button
                  onClick={() => rejectOpen(o.order_id)}
                  className="text-[12px] bg-[#E63946] text-white px-3 py-1 rounded-full"
                  data-testid={`reject-${o.order_id}`}
                >Reject</button>
              </div>
            </div>
            {o.image_url && <img src={o.image_url} alt="creative" className="max-h-24 rounded-lg mt-2" />}
            {o.destination_url && <div className="text-[11px] text-[#6B7280] mt-1 break-all">→ {o.destination_url}</div>}
          </div>
        ))}
        {rejectingId && (
          <div className="mt-3 p-3 border border-[#EEEEEE] rounded-lg" data-testid="reject-panel">
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection"
              className="w-full border border-[#EEEEEE] rounded p-2 text-sm"
              data-testid="reject-reason"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={rejectSubmit} className="text-[12px] bg-[#E63946] text-white px-3 py-1 rounded-full">
                Confirm Reject
              </button>
              <button onClick={() => setRejectingId("")} className="text-[12px] text-[#6B7280]">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* All orders */}
      <div className="card-soft p-5" data-testid="ss-all-orders">
        <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">All Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase text-[#6B7280] tracking-wider border-b border-[#EEEEEE]">
                <th className="py-2 pr-3">Order</th><th className="py-2 pr-3">Business</th>
                <th className="py-2 pr-3">Slot</th><th className="py-2 pr-3">Islands</th>
                <th className="py-2 pr-3">Dates</th><th className="py-2 pr-3">Price</th>
                <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Payment</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allOrders.map((o) => (
                <tr key={o.order_id} className="border-b border-[#F5F5F5]">
                  <td className="py-1.5 pr-3">{o.order_id}</td>
                  <td className="py-1.5 pr-3">{o.business_name}</td>
                  <td className="py-1.5 pr-3">{SLOT_LABEL[o.slot_type] || o.slot_type}</td>
                  <td className="py-1.5 pr-3">{(o.island_targeting || []).join(", ")}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{o.start_date}→{o.end_date}</td>
                  <td className="py-1.5 pr-3">${o.price_usd}</td>
                  <td className="py-1.5 pr-3">
                    <span style={{ color: STATUS_COLOR[o.status] || "#6B7280" }}>{o.status}</span>
                  </td>
                  <td className="py-1.5 pr-3">{o.payment_status}</td>
                  <td className="py-1.5 pr-3 flex gap-1">
                    {o.status === "active" && (
                      <button onClick={() => deactivate(o.order_id)} className="text-[10px] text-[#6B7280]">Deactivate</button>
                    )}
                    <button onClick={() => del(o.order_id)} className="text-[10px] text-[#E63946]">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing */}
      <div className="card-soft p-5" data-testid="ss-pricing">
        <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">Pricing (USD)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase text-[#6B7280] tracking-wider border-b border-[#EEEEEE]">
                <th className="py-2 pr-3">Slot</th><th className="py-2 pr-3">Duration</th>
                <th className="py-2 pr-3">Price</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pricing.map((p, i) => (
                <PricingRow key={`${p.slot_type}-${p.duration_weeks}`} row={p} onSave={savePrice} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Testimonials */}
      <div className="card-soft p-5" data-testid="ss-testimonials">
        <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">Testimonials</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <input value={tNew.business_name} placeholder="Business name"
                 onChange={(e) => setTNew({ ...tNew, business_name: e.target.value })}
                 className="border border-[#EEEEEE] rounded px-2 py-1.5 text-sm" data-testid="test-new-name" />
          <input value={tNew.island} placeholder="Island"
                 onChange={(e) => setTNew({ ...tNew, island: e.target.value })}
                 className="border border-[#EEEEEE] rounded px-2 py-1.5 text-sm" data-testid="test-new-island" />
          <input value={tNew.quote} placeholder="Quote"
                 onChange={(e) => setTNew({ ...tNew, quote: e.target.value })}
                 className="border border-[#EEEEEE] rounded px-2 py-1.5 text-sm" data-testid="test-new-quote" />
        </div>
        <button onClick={addTest} className="text-[12px] bg-[#0AADA0] text-white px-3 py-1.5 rounded-full">
          + Add Testimonial
        </button>
        <div className="mt-3 space-y-2">
          {testimonials.map((t) => (
            <div key={t.id} className="flex items-center justify-between border border-[#EEEEEE] rounded p-2 text-[12px]">
              <div><b>{t.business_name}</b>{t.island ? ` · ${t.island}` : ""} — “{t.quote}”</div>
              <button onClick={() => delTest(t.id)} className="text-[#E63946]">Remove</button>
            </div>
          ))}
        </div>
      </div>

      {/* Advertisers */}
      <div className="card-soft p-5" data-testid="ss-advertisers">
        <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">Advertiser Accounts</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase text-[#6B7280] tracking-wider border-b border-[#EEEEEE]">
                <th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Business</th>
                <th className="py-2 pr-3">Joined</th><th className="py-2 pr-3">Campaigns</th>
                <th className="py-2 pr-3">Spend</th>
              </tr>
            </thead>
            <tbody>
              {advertisers.map((a) => (
                <tr key={a.email} className="border-b border-[#F5F5F5]">
                  <td className="py-1.5 pr-3">{a.email}</td>
                  <td className="py-1.5 pr-3">{a.business_name}</td>
                  <td className="py-1.5 pr-3">{(a.created_at || "").slice(0,10)}</td>
                  <td className="py-1.5 pr-3">{a.campaign_count}</td>
                  <td className="py-1.5 pr-3">${a.total_spend_usd || 0} USD</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, v }) => (
  <div className="border border-[#EEEEEE] rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
    <div className="text-[15px] font-medium text-[#1A2E35] mt-1">{v}</div>
  </div>
);

const BarBlock = ({ title, data }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">{title}</div>
      <div className="space-y-1">
        {entries.length === 0 ? (
          <div className="text-[12px] text-[#6B7280]">No data yet.</div>
        ) : entries.map(([k, v]) => (
          <div key={k} className="text-[11px]">
            <div className="flex justify-between mb-0.5">
              <span className="text-[#1A2E35]">{k}</span>
              <span className="text-[#6B7280]">${v} USD</span>
            </div>
            <div className="h-1.5 bg-[#F5F5F5] rounded-full overflow-hidden">
              <div className="h-full bg-[#0AADA0]" style={{ width: `${(v / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PricingRow = ({ row, onSave }) => {
  const [price, setPrice] = useState(row.price_usd);
  return (
    <tr className="border-b border-[#F5F5F5]">
      <td className="py-1.5 pr-3">{SLOT_LABEL[row.slot_type] || row.slot_type}</td>
      <td className="py-1.5 pr-3">{row.duration_weeks === 4 ? "1 Month" : `${row.duration_weeks} Week${row.duration_weeks>1?"s":""}`}</td>
      <td className="py-1.5 pr-3">
        <input
          type="number" value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-24 border border-[#EEEEEE] rounded px-2 py-1 text-sm"
          data-testid={`price-${row.slot_type}-${row.duration_weeks}`}
        />
      </td>
      <td>
        <button
          onClick={() => onSave({ slot_type: row.slot_type, duration_weeks: row.duration_weeks,
                                   price_usd: parseFloat(price), is_active: true })}
          className="text-[11px] text-[#0AADA0]"
          data-testid={`save-price-${row.slot_type}-${row.duration_weeks}`}
        >Save</button>
      </td>
    </tr>
  );
};
